/**
 * SSO mot Sigge via Office.auth.getAccessToken — samma som Outlook Add-in.
 */

const ACCESS_KEY = "sigge_access";
const REFRESH_KEY = "sigge_refresh";

export interface SiggeUser {
  id: number;
  email: string;
  full_name: string;
  role: string;
}

export interface SsoResult {
  access: string;
  refresh: string;
  user: SiggeUser;
}

const SIGGE_API_URL =
  (import.meta.env.VITE_SIGGE_API_URL as string | undefined) ||
  "https://app.sigge.io";

export const tokenStore = {
  get access(): string | null {
    return sessionStorage.getItem(ACCESS_KEY);
  },
  set(result: SsoResult): void {
    sessionStorage.setItem(ACCESS_KEY, result.access);
    sessionStorage.setItem(REFRESH_KEY, result.refresh);
  },
  clear(): void {
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  },
};

export async function signInWithOffice(
  { silent = false }: { silent?: boolean } = {}
): Promise<SsoResult> {
  let idToken: string;
  try {
    idToken = await Office.auth.getAccessToken({
      // silent=true används av authFetch när Sigge-JWT:n har expirerat
      // efter t.ex. maskin-sömn. Office ger då tillbaka en färsk token
      // utan UI-prompt om Office-sessionen fortfarande är giltig; om
      // en prompt hade behövts kastas TokenNeededError och authFetch
      // låter felet bubbla — då landar juristen på Logga in-vyn.
      allowSignInPrompt: !silent,
      allowConsentPrompt: !silent,
      forMSGraphAccess: false,
    });
  } catch (err) {
    throw new Error(
      `Office SSO misslyckades: ${(err as { message?: string }).message ?? err}`
    );
  }

  const res = await fetch(`${SIGGE_API_URL}/api/integrations/microsoft/sso/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }

  const result = (await res.json()) as SsoResult;
  tokenStore.set(result);
  return result;
}

/**
 * Wrappar fetch med automatisk token-refresh vid 401. Skäl: Sigge-
 * JWT:n har kort livslängd; efter t.ex. över-natten-sömn på maskinen
 * är den utgången och backend kastar "Givet token är inte giltigt
 * för någon tokentyp". Utan wrapper skulle juristen behöva klicka
 * Logga in igen — även om Office-sessionen fortfarande är giltig.
 *
 * Flödet:
 *   1. Kör fetch med nuvarande access-token.
 *   2. Om 401: tystcasa signInWithOffice({silent:true}) → ny SSO →
 *      ny Sigge-JWT sparas i tokenStore.
 *   3. Kör om fetch en gång med den nya token:en.
 *   4. Om även retry:t ger 401 (eller silent-SSO failade) → returnera
 *      det ursprungliga 401-svaret så anroparen kan visa fel.
 */
export async function authFetch(
  url: string,
  init: RequestInit = {}
): Promise<Response> {
  const call = (): Promise<Response> => {
    const tok = tokenStore.access;
    const headers = new Headers(init.headers ?? {});
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
    return fetch(url, { ...init, headers });
  };
  let res = await call();
  if (res.status !== 401) return res;

  // 401 → försök silent Office SSO-refresh en gång.
  try {
    await signInWithOffice({ silent: true });
  } catch {
    return res; // silent-refresh failade — låt anroparen se 401:n
  }
  return await call();
}

export function getApiBase(): string {
  return SIGGE_API_URL;
}
